import scssVariables from "@/assets/styles/variables.module.scss"
import { isPresent } from "@/lib/utils"

import { BLOCK_TYPES, getBlockTitle } from "@shared/blocks"
import {
  BLOCK_DATA_FIELD_KEYS,
  findDataField,
  hasPageDataOverride,
  slotFieldSlug
} from "@shared/page-data"

export const BLOCK_DEFAULT_HEIGHT_RATIO = 100

export const ROLES = {
  PAGE: "page",
  BLOCK: "block",
  BLOCK_CONTAINER: "block-container"
}

export const APP_COLORS = {
  WHITE: "white",
  LIGHT_GREY: "light_grey",
  GREY: "grey",
  BLACK: "black"
}

export const BRAND_COLORS = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  SECONDARY_2: "secondary_2",
  SECONDARY_3: "secondary_3",
  DARK: "dark",
  LIGHT: "light"
}

export const ALL_COLORS = {
  ...APP_COLORS,
  ...BRAND_COLORS
}

export const COLOR_MODES = {
  LIGHT: "light",
  DARK: "dark"
}

export const BACKGROUND_COLORS = {
  [APP_COLORS.WHITE]: scssVariables["color-fixed--white"],
  [APP_COLORS.LIGHT_GREY]: scssVariables["color-fixed--grey-20"],
  [APP_COLORS.GREY]: scssVariables["color-fixed--grey-60"],
  [APP_COLORS.BLACK]: scssVariables["color-fixed--black"],
  [BRAND_COLORS.PRIMARY]: "var(--primary-brand-color-hex)",
  [BRAND_COLORS.SECONDARY]: "var(--secondary-brand-color-hex)",
  [BRAND_COLORS.SECONDARY_2]: "var(--secondary-2-brand-color-hex)",
  [BRAND_COLORS.SECONDARY_3]: "var(--secondary-3-brand-color-hex)",
  [BRAND_COLORS.DARK]: "var(--dark-brand-color-hex)",
  [BRAND_COLORS.LIGHT]: "var(--light-brand-color-hex)"
}

export const TEXT_COLORS = {
  [APP_COLORS.WHITE]: scssVariables["color-ui--white"],
  [APP_COLORS.LIGHT_GREY]: scssVariables["color-ui--grey-20"],
  [APP_COLORS.GREY]: scssVariables["color-ui--grey-70"],
  [APP_COLORS.BLACK]: scssVariables["color-ui--grey-90"],
  [BRAND_COLORS.PRIMARY]: "var(--primary-brand-color-hex)",
  [BRAND_COLORS.SECONDARY]: "var(--secondary-brand-color-hex)",
  [BRAND_COLORS.SECONDARY_2]: "var(--secondary-2-brand-color-hex)",
  [BRAND_COLORS.SECONDARY_3]: "var(--secondary-3-brand-color-hex)",
  [BRAND_COLORS.DARK]: "var(--dark-brand-color-hex)",
  [BRAND_COLORS.LIGHT]: "var(--light-brand-color-hex)"
}

export const LIVESTREAM_PROVIDERS = {
  YOUTUBE: "youtube",
  ZOOM: "zoom",
  VIMEO: "vimeo",
  FACEBOOK: "facebook"
}

export const DYNAMIC_ORDER_BY = ["created_desc", "created_asc", "alphabetical"]

export const buildBlockMountedEvent = (getBlock, element) => ({
  getBlock,
  element
})

const isPageDataFieldOverridden = (pageData = {}, pageDataField = null) =>
  !!pageDataField?.built_in ||
  hasPageDataOverride(pageData, pageDataField?.slug) ||
  hasPageDataOverride(pageData, pageDataField?.slot_slug)

export const resolvePageDataValue = (
  pageData = {},
  {
    explicitSlug = null,
    slotSlug = null,
    fallbackValue = null,
    builtInFieldContext = {}
  } = {}
) => {
  const pageDataField = findDataField(
    pageData,
    {
      slug: explicitSlug,
      slotSlug
    },
    builtInFieldContext
  )

  if (isPageDataFieldOverridden(pageData, pageDataField)) {
    return pageDataField.value
  }

  return isPresent(fallbackValue) ? fallbackValue : null
}

export const getBlockImage = (block, pageData = {}) => {
  return resolvePageDataValue(pageData, {
    explicitSlug: block?.data?.[BLOCK_DATA_FIELD_KEYS.IMAGE_PAGE_ATTRIBUTE],
    slotSlug: block?.uuid ? slotFieldSlug(block.uuid, "image") : null,
    fallbackValue: block?.data?.image
  })
}

export const getBlockVideo = (block, pageData = {}) => {
  return resolvePageDataValue(pageData, {
    explicitSlug: block?.data?.[BLOCK_DATA_FIELD_KEYS.VIDEO_PAGE_ATTRIBUTE],
    slotSlug: block?.uuid ? slotFieldSlug(block.uuid, "video") : null,
    fallbackValue: block?.data?.video
  })
}

export const getBlockAudio = (block, pageData = {}) => {
  return resolvePageDataValue(pageData, {
    explicitSlug: block?.data?.[BLOCK_DATA_FIELD_KEYS.AUDIO_PAGE_ATTRIBUTE],
    slotSlug: block?.uuid ? slotFieldSlug(block.uuid, "audio") : null,
    fallbackValue: block?.data?.audio
  })
}

export const getBlockItems = block => {
  return isPresent(block.data.collection)
    ? block.data.collection?.items || []
    : []
}

export const getBlockDescendants = (
  block,
  blockType = null, // Leave null to include all types
  includeSelf = false,
  acc = []
) => {
  if (!block) return acc

  if (includeSelf && (!blockType || block.type === blockType)) {
    acc.push(block)
  }

  if (block?.blocks) {
    block.blocks.map(b => getBlockDescendants(b, blockType, true, acc))
  }

  return acc
}

export const decodeBlockAction = data => JSON.parse(atob(data))

export const findBlockByType = (blocks, type) => {
  if (!blocks || !blocks.length || !type) return null

  for (const b of blocks) {
    if (b.type === type) {
      return b
    }

    const block = findBlockByType(b.blocks, type)
    if (block) {
      return block
    }
  }

  return null
}

export { BLOCK_TYPES, getBlockTitle }
